import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SignatureRequestsForm = ({ caseId, client, agent, onUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);  // { kind:'success'|'error', message, results, skipped }
  const [form, setForm] = useState({
    signer_role: "client",
    signer_name: "",
    signer_email: "",
  });

  const fetchRequests = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/compliance/cases/${caseId}/signature-requests`);
      if (r.ok) setRequests(await r.json());
    } catch (e) { console.error('Failed to fetch signature requests:', e); }
  }, [caseId]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const sendBatch = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const res = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/signature-requests/batch?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Iter 53 — surface real backend reason rather than generic message.
        const reason = err.detail
          || (Array.isArray(err) && err[0]?.msg)
          || `Failed to send signature requests (HTTP ${res.status})`;
        throw new Error(reason);
      }
      const data = await res.json();
      // Iter 53 — even on HTTP 200 some recipients may have bounced; show that.
      const failed = (data.results || []).filter(r => r.email_status !== "sent" && r.email_status !== "skipped (no SMTP configured)");
      const sent = (data.results || []).filter(r => r.email_status === "sent");
      let kind = "success";
      let message = `Created ${data.results?.length || 0} signature request(s).`;
      if (failed.length && !sent.length) {
        kind = "error";
        message = `Sent failed for all ${failed.length} recipient(s). Reason: ${failed[0]?.email_error || "SMTP rejected the message"}.`;
      } else if (failed.length) {
        kind = "warning";
        message = `Sent OK to ${sent.length}, failed for ${failed.length}. Click each row for details.`;
      }
      setLastResult({
        kind,
        message,
        results: data.results || [],
        skipped: data.skipped || [],
      });
      fetchRequests();
      onUpdate && onUpdate();
    } catch (e) {
      setLastResult({ kind: "error", message: e.message, results: [], skipped: [] });
    } finally { setLoading(false); }
  };

  const send = async () => {
    if (!form.signer_name || !form.signer_email) {
      setLastResult({ kind: "error", message: "Signer name and email are required.", results: [], skipped: [] });
      return;
    }
    // Iter 53 — basic email format check so we don't bounce off the backend
    // 422 for a typo. Surfaces the real reason inline.
    const trimmedEmail = form.signer_email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setLastResult({ kind: "error", message: `"${trimmedEmail}" doesn't look like a valid email. Please double-check the address.`, results: [], skipped: [] });
      return;
    }
    setLoading(true);
    setLastResult(null);
    try {
      const res = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/signature-requests?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, signer_email: trimmedEmail }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Iter 53 — surface the *backend's* validation detail directly so
        // the advisor sees "Invalid email address" instead of a generic
        // "Failed to send signature request".
        const reason = err.detail
          || (Array.isArray(err) && err[0]?.msg)
          || `Failed to send signature request (HTTP ${res.status})`;
        throw new Error(reason);
      }
      const data = await res.json();
      // Copy URL to clipboard for quick share
      try { await navigator.clipboard.writeText(data.signing_url); } catch (_e) { console.warn('Clipboard unavailable — signing URL not auto-copied:', _e); }
      setLastResult({
        kind: "success",
        message: `Signature request sent to ${form.signer_email}. Signing URL copied to clipboard.`,
        results: [{
          signer_role: form.signer_role,
          signer_email: form.signer_email,
          email_status: data.signature_request?.email_status || "sent",
          email_error: data.signature_request?.email_error || null,
        }],
        skipped: [],
      });
      setShowAdd(false);
      setForm({ signer_role: "client", signer_name: "", signer_email: "" });
      fetchRequests();
      onUpdate && onUpdate();
    } catch (e) {
      setLastResult({ kind: "error", message: e.message, results: [], skipped: [] });
    } finally { setLoading(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this signature request?")) return;
    await fetch(
      `${API_URL}/api/compliance/cases/${caseId}/signature-requests/${id}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
      { method: "DELETE" }
    );
    fetchRequests();
  };

  const hardDelete = async (id) => {
    if (!window.confirm("Permanently DELETE this signature request? This cannot be undone.")) return;
    await fetch(
      `${API_URL}/api/compliance/cases/${caseId}/signature-requests/${id}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}&hard=true`,
      { method: "DELETE" }
    );
    fetchRequests();
    onUpdate && onUpdate();
  };

  const copyUrl = async (token) => {
    const origin = window.location.origin;
    const url = `${origin}/sign/${token}`;
    try { await navigator.clipboard.writeText(url); setLastResult({ kind: "success", message: `Signing URL copied to clipboard: ${url}`, results: [], skipped: [] }); }
    catch { prompt("Copy signing URL:", url); }
  };

  // Iter 64 — override / record the signed date (paper or backdated signatures).
  const setSignedDate = async (id, dateStr) => {
    try {
      const res = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/signature-requests/${id}/signed-date?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ signed_date: dateStr || null }) }
      );
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Failed to set date"); }
      fetchRequests();
      onUpdate && onUpdate();
    } catch (e) { alert(e.message); }
  };

  // Pre-fill default signer based on role
  const handleRoleChange = (role) => {
    let name = "", email = "";
    if (role === "client" && client) { name = client.full_name || ""; email = client.email || ""; }
    else if (role === "advisor" && agent) { name = agent.name || ""; email = agent.email || ""; }
    setForm({ ...form, signer_role: role, signer_name: name, signer_email: email });
  };

  const statusBadge = (status) => {
    const map = {
      pending: "bg-yellow-100 text-yellow-700",
      signed: "bg-green-100 text-green-700",
      expired: "bg-gray-200 text-gray-600",
      cancelled: "bg-red-100 text-red-700",
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${map[status] || map.pending}`;
  };

  return (
    <Card data-testid="signatures-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5" />E-Signatures</CardTitle>
        <div className="flex gap-2">
          <Button onClick={sendBatch} disabled={loading} size="sm" className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]" data-testid="send-to-client-and-advisor-btn">
            <Send className="h-4 w-4 mr-1" />{loading ? "Sending..." : "Send to Client & Advisor"}
          </Button>
          <Button onClick={() => setShowAdd(!showAdd)} size="sm" variant="outline" data-testid="add-signature-request-btn">
            <Mail className="h-4 w-4 mr-1" />Custom Request
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {lastResult && (
          <div
            className={`border rounded-lg p-3 text-sm ${
              lastResult.kind === "success"
                ? "bg-green-50 border-green-300 text-green-900"
                : "bg-red-50 border-red-300 text-red-900"
            }`}
            data-testid="signature-result-panel"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium">{lastResult.kind === "success" ? "✓ " : "⚠ "}{lastResult.message}</div>
              <button
                type="button"
                onClick={() => setLastResult(null)}
                className="text-xs opacity-60 hover:opacity-100"
                data-testid="dismiss-signature-result"
              >Dismiss ✕</button>
            </div>
            {lastResult.results?.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs">
                {lastResult.results.map((r, i) => {
                  const ok = r.email_status === "sent";
                  const failed = r.email_status === "failed";
                  return (
                    <li key={`${r.signer_role}-${r.signer_email}-${i}`} className="flex flex-wrap items-center gap-2" data-testid={`result-row-${r.signer_role}`}>
                      <span
                        className={`px-2 py-0.5 rounded-full font-medium ${
                          ok ? "bg-green-200 text-green-900" :
                          failed ? "bg-red-200 text-red-900" :
                          "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {ok ? "✅ Sent" : failed ? "⚠️ Failed" : "📭 No SMTP"}
                      </span>
                      <span className="font-medium capitalize">{r.signer_role}:</span>
                      <span className="font-mono">{r.signer_email}</span>
                      {r.email_error && <span className="text-red-700">— {r.email_error}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
            {lastResult.skipped?.length > 0 && (
              <div className="mt-2 text-xs">
                <div className="font-medium">Skipped:</div>
                <ul className="list-disc ml-5">
                  {lastResult.skipped.map((s, i) => (
                    <li key={i}><b>{s.role}</b>: {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            {lastResult.kind === "success" && lastResult.results?.some(r => r.email_status === "failed") && (
              <p className="mt-2 text-xs italic">
                Tip: a &quot;Failed&quot; status means the recipient email was either invalid, on our blocked-test-domain list (example.com, test.com, etc.), or Hostinger SMTP rejected it. Verify the email on the client/advisor record and try again.
              </p>
            )}
          </div>
        )}
        {showAdd && (
          <div className="border rounded-lg p-4 bg-blue-50 grid md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Signer Role</label>
              <select
                value={form.signer_role}
                onChange={(e) => handleRoleChange(e.target.value)}
                className="w-full px-2 py-2 border rounded"
                data-testid="sig-signer-role"
              >
                <option value="client">Client</option>
                <option value="advisor">Advisor</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Signer Name</label>
              <input
                value={form.signer_name}
                onChange={(e) => setForm({ ...form, signer_name: e.target.value })}
                className="w-full px-2 py-2 border rounded"
                placeholder="Full legal name"
                data-testid="sig-signer-name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Signer Email</label>
              <input
                type="email"
                value={form.signer_email}
                onChange={(e) => setForm({ ...form, signer_email: e.target.value })}
                className="w-full px-2 py-2 border rounded"
                placeholder="signer@example.com"
                data-testid="sig-signer-email"
              />
            </div>
            <div className="md:col-span-3 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={send} disabled={loading} className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]" data-testid="send-signature-request-btn">
                <Mail className="h-4 w-4 mr-2" />{loading ? "Sending..." : "Send Email & Get Link"}
              </Button>
            </div>
          </div>
        )}

        {requests.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No signature requests yet.</p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="border rounded-lg p-3 flex items-center justify-between" data-testid={`sig-req-${r.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium">{r.signer_name}</span>
                    <span className="text-xs text-gray-500">({r.signer_role})</span>
                    <span className={statusBadge(r.status)}>{r.status}</span>
                    {r.email_status && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          r.email_status === "sent" ? "bg-green-100 text-green-700" :
                          r.email_status === "failed" ? "bg-red-100 text-red-700" :
                          "bg-gray-100 text-gray-600"
                        }`}
                        title={r.email_error || (r.email_sent_at ? `Email delivered at ${new Date(r.email_sent_at).toLocaleString()}` : 'Email send status')}
                        data-testid={`email-status-${r.id}`}
                      >
                        {r.email_status === "sent" ? "✅ Email Sent" :
                         r.email_status === "failed" ? "⚠️ Email Failed" :
                         "📭 No Email"}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{r.signer_email}</p>
                  {r.email_error && (
                    <p className="text-xs text-red-600 mt-1">SMTP error: {r.email_error}</p>
                  )}
                  {r.signed_at && (
                    <p className="text-xs text-green-600 mt-1">
                      <CheckCircle className="h-3 w-3 inline mr-1" />
                      Signed {new Date(r.signed_at).toLocaleDateString()}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <label className="text-xs text-gray-500">Override signed date:</label>
                    <input
                      type="date"
                      value={r.signed_at ? new Date(r.signed_at).toISOString().slice(0, 10) : ''}
                      onChange={(e) => setSignedDate(r.id, e.target.value)}
                      className="px-2 py-1 border rounded text-xs"
                      data-testid={`sig-date-override-${r.id}`}
                    />
                    {r.signed_at && (
                      <button type="button" onClick={() => setSignedDate(r.id, '')} className="text-xs text-gray-400 hover:text-red-500" data-testid={`sig-date-clear-${r.id}`}>Clear</button>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => copyUrl(r.token)} title="Copy signing URL" data-testid={`copy-url-${r.id}`}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => cancel(r.id)} title="Cancel request" data-testid={`cancel-sig-${r.id}`}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => hardDelete(r.id)} title="Permanently delete" data-testid={`delete-sig-${r.id}`}>
                    <Trash2 className="h-4 w-4 text-red-700" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// =============================================================
// Electronic Communication Consent
// =============================================================
